import React from 'react';
import useLabelingStore from '../store/useDetectionStore';
import Button from '../../../../../components/Atoms/Button.jsx';

/**
 * Canvas toolbar: tools, history, view options, zoom and image navigation.
 *
 * Everything is grouped and labelled with its shortcut so the toolbar stays
 * readable at a glance instead of being one long row of identical grey icons.
 */

const TOOLS = [
    { id: 'select', icon: 'fa-mouse-pointer', label: 'Select', hint: 'Select and edit (V)' },
    { id: 'draw', icon: 'fa-vector-square', label: 'Box', hint: 'Draw bounding box (B)' },
    { id: 'polygon', icon: 'fa-draw-polygon', label: 'Polygon', hint: 'Draw polygon (G)' },
    { id: 'pan', icon: 'fa-hand-paper', label: 'Pan', hint: 'Pan the image (H, or hold Space)' },
];

const DetectionMiddleTop = ({ undo, redo, canUndo, canRedo }) => {
    const {
        setMode,
        mode,
        getCurrentImage,
        startPolygon,
        isDrawingPolygon,
        prevImage,
        currentImageIndex,
        images,
        nextImage,
        zoomIn,
        zoomOut,
        zoomToFit,
        zoomToActual,
        zoomLevel,
        isCrosshairEnabled,
        setCrosshairEnabled,
        showLabels,
        setShowLabels,
        showFill,
        setShowFill,
        autoMode,
        setAutoMode,
    } = useLabelingStore();

    const hasImage = !!getCurrentImage();
    const total = images.length;

    const selectTool = (toolId) => {
        if (toolId === 'polygon') {
            startPolygon();
            setMode('polygon');
            return;
        }
        setMode(toolId);
    };

    return (
        <div className="lf-toolbar">
            <div className="lf-toolbar__group lf-toolbar__group--segmented" role="group" aria-label="Tools">
                {TOOLS.map((tool) => (
                    <Button
                        key={tool.id}
                        className={`lf-tool-btn ${mode === tool.id ? 'lf-tool-btn--active' : ''}`}
                        onClick={() => selectTool(tool.id)}
                        disabled={tool.id !== 'select' && !hasImage}
                        tooltipText={tool.hint}
                    >
                        <i className={`fa ${tool.icon}`} aria-hidden="true"></i>
                        <span className="lf-tool-btn__label">{tool.label}</span>
                    </Button>
                ))}
            </div>

            <div className="lf-toolbar__group" role="group" aria-label="History">
                <Button
                    className="lf-tool-btn lf-tool-btn--icon"
                    onClick={() => undo?.()}
                    disabled={!canUndo}
                    tooltipText="Undo (Ctrl+Z)"
                >
                    <i className="fa fa-undo" aria-hidden="true"></i>
                </Button>
                <Button
                    className="lf-tool-btn lf-tool-btn--icon"
                    onClick={() => redo?.()}
                    disabled={!canRedo}
                    tooltipText="Redo (Ctrl+Y)"
                >
                    <i className="fa fa-redo" aria-hidden="true"></i>
                </Button>
            </div>

            <div className="lf-toolbar__group" role="group" aria-label="View options">
                <Button
                    className={`lf-tool-btn lf-tool-btn--icon ${showLabels ? 'lf-tool-btn--active' : ''}`}
                    onClick={() => setShowLabels(!showLabels)}
                    tooltipText="Show class names on boxes (L)"
                >
                    <i className="fa fa-tag" aria-hidden="true"></i>
                </Button>
                <Button
                    className={`lf-tool-btn lf-tool-btn--icon ${showFill ? 'lf-tool-btn--active' : ''}`}
                    onClick={() => setShowFill(!showFill)}
                    tooltipText="Tint box interiors — easier to spot small boxes (F)"
                >
                    <i className="fa fa-fill-drip" aria-hidden="true"></i>
                </Button>
                <Button
                    className={`lf-tool-btn lf-tool-btn--icon ${isCrosshairEnabled ? 'lf-tool-btn--active' : ''}`}
                    onClick={() => setCrosshairEnabled(!isCrosshairEnabled)}
                    tooltipText="Crosshair guides while drawing (C)"
                >
                    <i className="fa fa-crosshairs" aria-hidden="true"></i>
                </Button>
            </div>

            <div className="lf-toolbar__spacer" />

            <div className="lf-toolbar__group" role="group" aria-label="Zoom">
                <Button
                    className="lf-tool-btn lf-tool-btn--icon"
                    onClick={zoomOut}
                    disabled={!hasImage}
                    tooltipText="Zoom out (−)"
                >
                    <i className="fa fa-search-minus" aria-hidden="true"></i>
                </Button>
                <span className="lf-zoom-readout" title="Current zoom">
                    {Math.round(zoomLevel * 100)}%
                </span>
                <Button
                    className="lf-tool-btn lf-tool-btn--icon"
                    onClick={zoomIn}
                    disabled={!hasImage}
                    tooltipText="Zoom in (+)"
                >
                    <i className="fa fa-search-plus" aria-hidden="true"></i>
                </Button>
                <Button
                    className="lf-tool-btn lf-tool-btn--icon"
                    onClick={zoomToFit}
                    disabled={!hasImage}
                    tooltipText="Fit image to the panel (0)"
                >
                    <i className="fa fa-expand-arrows-alt" aria-hidden="true"></i>
                </Button>
                <Button
                    className="lf-tool-btn lf-tool-btn--ratio"
                    onClick={zoomToActual}
                    disabled={!hasImage}
                    tooltipText="Actual size, 100% (1)"
                >
                    1:1
                </Button>
            </div>

            <div className="lf-toolbar__group" role="group" aria-label="Image navigation">
                <Button
                    className="lf-tool-btn lf-tool-btn--icon"
                    onClick={prevImage}
                    disabled={currentImageIndex === 0 || total === 0}
                    tooltipText="Previous image (A / ←)"
                >
                    <i className="fa fa-angle-left" aria-hidden="true"></i>
                </Button>
                <span className="lf-image-counter">
                    {total === 0 ? '0 / 0' : `${currentImageIndex + 1} / ${total}`}
                </span>
                <Button
                    className="lf-tool-btn lf-tool-btn--icon"
                    onClick={nextImage}
                    disabled={currentImageIndex === total - 1 || total === 0}
                    tooltipText="Next image (D / →)"
                >
                    <i className="fa fa-angle-right" aria-hidden="true"></i>
                </Button>
            </div>

            <div className="lf-toolbar__group">
                <Button
                    className={`lf-tool-btn lf-tool-btn--ai ${autoMode ? 'lf-tool-btn--active' : ''}`}
                    onClick={() => setAutoMode(!autoMode)}
                    disabled={!hasImage}
                    tooltipText="AI auto label (auto-detect objects)"
                >
                    <i className="fa fa-robot" aria-hidden="true"></i>
                    <span className="lf-tool-btn__label">AI</span>
                </Button>
            </div>

            {isDrawingPolygon && (
                <span className="lf-toolbar__status">
                    <i className="fa fa-draw-polygon" aria-hidden="true"></i> Drawing polygon…
                </span>
            )}
        </div>
    );
};

export default DetectionMiddleTop;
